import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { UserPlus, Trash2, Users, Ban, CheckCircle2, Shield, Pencil, X } from "lucide-react";

function EditAgentModal({ agent, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: agent.name || "",
    email: agent.email || "",
    password: "",
    is_active: !!agent.is_active,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch = {};
      if (form.name !== agent.name) patch.name = form.name;
      if (form.email.trim().toLowerCase() !== (agent.email || "").toLowerCase()) {
        patch.email = form.email.trim().toLowerCase();
      }
      if (form.is_active !== agent.is_active) patch.is_active = form.is_active;
      if (form.password.trim()) patch.password = form.password;

      if (Object.keys(patch).length === 0) {
        toast.info("Nothing to update");
        setSaving(false);
        return;
      }
      await api.patch(`/agents/${agent.id}`, patch);
      toast.success("Agent updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Update failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="admin-agent-edit-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Edit agent</div>
            <h3 className="font-display text-lg font-bold text-slate-900">{agent.name}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            data-testid="admin-agent-edit-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Full name</label>
            <input
              required
              className="beacon-input"
              data-testid="admin-agent-edit-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Email</label>
            <input
              required
              type="email"
              className="beacon-input"
              data-testid="admin-agent-edit-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <div className="text-[11px] text-slate-500 mt-1">The agent will use this email to sign in.</div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">New password</label>
            <input
              type="password"
              minLength={6}
              className="beacon-input"
              placeholder="Leave blank to keep current password"
              data-testid="admin-agent-edit-password-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 mt-1">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              data-testid="admin-agent-edit-active-toggle"
            />
            Active — can sign in and be assigned tickets
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="admin-agent-edit-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="admin-agent-edit-save-btn"
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [agentCount, setAgentCount] = useState(0);
  const [maxAgents, setMaxAgents] = useState(5);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agents");
      setAgents(data.agents);
      setAgentCount(data.agent_count);
      setMaxAgents(data.max_agents);
    } catch (err) {
      toast.error("Could not load agents", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/agents", form);
      toast.success("Agent created", { description: form.email });
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error("Could not create agent", { description: formatApiError(err) });
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (agent) => {
    try {
      await api.patch(`/agents/${agent.id}`, { is_active: !agent.is_active });
      toast.success(agent.is_active ? "Agent disabled" : "Agent enabled");
      load();
    } catch (err) {
      toast.error("Update failed", { description: formatApiError(err) });
    }
  };

  const remove = async (agent) => {
    if (!window.confirm(`Remove agent ${agent.name}? Their tickets will be unassigned.`)) return;
    try {
      await api.delete(`/agents/${agent.id}`);
      toast.success("Agent removed");
      load();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  const atLimit = agentCount >= maxAgents;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight">Agent seats</h1>
          <p className="text-sm text-slate-500">Manage the support team — up to {maxAgents} agents.</p>
        </div>
        <div
          data-testid="admin-agent-count-badge"
          className={`rounded-xl px-4 py-2.5 border font-semibold flex items-center gap-2 ${
            atLimit ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"
          }`}
        >
          <Users className="w-4 h-4" />
          <span className="font-mono">{agentCount} / {maxAgents}</span>
          <span className="text-xs font-normal">seats used</span>
        </div>
      </div>

      <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-7 shadow-xl">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold">Add a new agent</h2>
        </div>
        {atLimit ? (
          <div className="text-sm bg-rose-500/10 border border-rose-400/30 text-rose-100 rounded-lg px-4 py-3">
            You've hit the {maxAgents}-agent maximum. Remove or disable an existing agent to free a seat.
          </div>
        ) : (
          <form onSubmit={create} className="grid sm:grid-cols-4 gap-3">
            <input required className="beacon-input bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" placeholder="Full name"
              data-testid="admin-agent-name-input"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input required type="email" className="beacon-input bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" placeholder="Email"
              data-testid="admin-agent-email-input"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input required minLength={6} type="password" className="beacon-input bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" placeholder="Password (min 6)"
              data-testid="admin-agent-password-input"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <Button type="submit" disabled={creating} data-testid="admin-agent-create-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg h-10">
              {creating ? "Adding..." : "Add agent"}
            </Button>
          </form>
        )}
      </div>

      <div className="beacon-card divide-y divide-slate-200">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading team...</div>
        ) : agents.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No team members yet.</div>
        ) : (
          agents.map((a) => (
            <div key={a.id} className="p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-agent-row-${a.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-xl grid place-items-center font-semibold ${
                  a.role === "admin" ? "bg-slate-900 text-white" : "bg-blue-100 text-blue-700"
                }`}>
                  {a.role === "admin" ? <Shield className="w-5 h-5" /> : a.name?.[0]?.toUpperCase() || "A"}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{a.name}</div>
                  <div className="text-xs text-slate-500 truncate">{a.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`beacon-badge ${
                  a.role === "admin" ? "bg-slate-100 text-slate-700 border-slate-200" : "bg-blue-50 text-blue-700 border-blue-200"
                }`}>
                  {a.role}
                </span>
                <span className={`beacon-badge ${
                  a.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"
                }`}>
                  <span className={`beacon-dot ${a.is_active ? "bg-emerald-500" : "bg-slate-400"}`} />
                  {a.is_active ? "active" : "disabled"}
                </span>
                {a.role === "agent" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setEditing(a)} data-testid={`admin-agent-edit-${a.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(a)} data-testid={`admin-agent-toggle-${a.id}`}>
                      {a.is_active ? <><Ban className="w-3.5 h-3.5 mr-1" /> Disable</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Enable</>}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      onClick={() => remove(a)} data-testid={`admin-agent-delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {editing && (
        <EditAgentModal
          agent={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
